import React, { useState } from "react";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import * as XLSX from "xlsx";
import UniversalSearchServices from "../../../services/UniversalSearchServices";

const UniversalSearch = () => {
    const [openSections, setOpenSections] = useState({
        master: true,      // default open
        inventory: true,
        items: false,
        miv: false,
        transfer: false,
        casting: false,
        asset: false,
        fabrication: false,
    });

    const [showInventoryExtraCols, setShowInventoryExtraCols] = useState(false);

    const [searched, setSearched] = useState(false);
    const [open2, setOpen2] = useState(true);
    const [searchType, setSearchType] = useState("partNo");
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState({
        masterResults: [],
        assetResults: [],
        inventoryResults: [],
        itemResults: [],
        mivResults: [],
        transferResults: [],
        castingResults: [],
        fabricationResults: [],
    });




    // ✅ Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        // 🔁 RESET inventory table state on new search
        setShowInventoryExtraCols(false);
        if (!query.trim()) {
            Swal.fire("Warning", "Please enter a search query", "warning");
            return;
        }

        try {
            setLoading(true);
            const response = await UniversalSearchServices.universalSearch({
                type: searchType,
                query: query.trim(),
            });

            const result = response?.data;
            if (response?.success && result) {
                setResults({
                    masterResults: result?.masterResults || [],
                    inventoryResults: result?.inventoryResults || [],
                    itemResults: result?.itemResults || [],
                    mivResults: result?.mivResults || [],
                    transferResults: result?.transferResults || [],
                    castingResults: result?.castingResults || [],
                    assetResults: result?.assetResults || [],
                    fabricationResults: result?.fabricationResults || [],
                });

                const totalCount =
                    (result?.masterResults?.length || 0) +
                    (result?.inventoryResults?.length || 0) +
                    (result?.itemResults?.length || 0) +
                    (result?.mivResults?.length || 0) +
                    (result?.transferResults?.length || 0) +
                    (result?.castingResults?.length || 0) +
                    (result?.assetResults?.length || 0) +
                    (result?.fabricationResults?.length || 0)


                if (totalCount > 0) {
                    Swal.fire("Success", `Found ${response?.count} result(s)`, "success");
                } else {
                    Swal.fire("No Results", "No records found", "info");
                }
            } else {
                setResults({
                    masterResults: [],
                    inventoryResults: [],
                    itemResults: [],
                    mivResults: [],
                    transferResults: [],
                    castingResults: [],
                    fabricationResults: [],
                });
                Swal.fire("No Results", "No records found", "info");
            }
            setSearched(true);
        } catch (err) {
            console.error("Universal Search Error:", err);
            Swal.fire("Error", "Something went wrong", "error");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Export to Excel
    const handleDownloadXLSX = () => {

        const wb = XLSX.utils.book_new();

        // ✅ Helper Function
        const createSheet = (data, sheetName) => {

            const wsData =
                data && data.length > 0
                    ? data
                    : [{ Message: "No Data Found" }];

            const ws = XLSX.utils.json_to_sheet(wsData);

            XLSX.utils.book_append_sheet(wb, ws, sheetName);
        };

        // =====================================================
        // MASTER SHEET
        // =====================================================

        createSheet(

            results.masterResults.map((row) => ({
                "Part No": row.partNo,
                Description: row.description,
                Category: row?.category?.name,
                Unit: row?.unit?.name,
                Rate: row.rate,
            })),

            "Master Items"
        );

        // =====================================================
        // INVENTORY SHEET
        // =====================================================

        createSheet(

            results.inventoryResults.map((row, idx) => ({
                "#": idx + 1,
                "Part No": row?.masterItem?.partNo,
                Description: row?.masterItem?.description,
                Store: row?.store?.name,
                Received: row?.totalRecive?.toFixed(2),
                Transfer: row?.transfer?.toFixed(2),
                "Current Stock": row?.currentStock?.toFixed(2),
                Miv: row?.totalMiv?.toFixed(2),
                "Transition Qty": row?.transitionQty?.toFixed(2),
            })),

            "Inventory"
        );

        // =====================================================
        // ITEM SHEET
        // =====================================================

        createSheet(

            results.itemResults.map((row, idx) => ({
                "#": idx + 1,
                "Req No": row?.requisitionNo?.requisitionNo,
                "Part No": row?.inventory?.masterItem?.partNo,
                Description: row?.inventory?.masterItem?.description,
                User: row?.user?.name,
                Store: row?.store?.name,
                "Qty Required": row?.qtyRequired,
                Approved: row?.approveQty,
                Received: row?.qtyRecived,
                Status: row?.status,
                PO: row?.po?.poNumber || "N/A",
                MRV: row?.mrv?.mrvNo || "N/A",
            })),

            "Items"
        );

        // =====================================================
        // MIV SHEET
        // =====================================================

        createSheet(

            results.mivResults.map((row, idx) => ({
                "#": idx + 1,
                "MIV No": row?.miv?.mivNo,
                "Part No": row?.inventory?.masterItem?.partNo,
                Description: row?.inventory?.masterItem?.description,
                User: row?.user?.name,
                Store: row?.miv?.store?.name,
                "Used Qty": row?.usedQty,
                "Approved Qty": row?.approveQty,
                Status: row?.status,
            })),

            "MIV"
        );

        // =====================================================
        // TRANSFER SHEET
        // =====================================================

        createSheet(

            results.transferResults.map((row, idx) => ({
                "#": idx + 1,
                "Transfer No": row?.transfer?.transferNo,
                "Part No": row?.inventory?.masterItem?.partNo,
                Description: row?.inventory?.masterItem?.description,
                User: row?.user?.name,
                From: row?.transfer?.storeFrom?.name,
                To: row?.transfer?.storeTo?.name,
                "Transfer Qty": row?.transferQty,
                "Approved Qty": row?.approveQty,
                "Received Qty": row?.recived,
                Status: row?.status,
                MRV: row?.mrv?.mrvNo,
            })),

            "Transfers"
        );

        // =====================================================
        // CASTING SHEET
        // =====================================================

        createSheet(

            results.castingResults.map((row, idx) => ({
                "#": idx + 1,
                "Request No": row?.RequestNo,
                "Request Type": row?.RequestType,
                "Part No": row?.inventory?.masterItem?.partNo,
                Description: row?.inventory?.masterItem?.description,
                User: row?.user?.name,
                Store: row?.storeName,
                Approved: row?.approveQty,
                Received: row?.qtyRecived,
                Casting: row?.castingQty,
            })),

            "Casting"
        );

        // =====================================================
        // FABRICATION SHEET
        // =====================================================

        createSheet(

            results.fabricationResults.map((row, idx) => ({

                "#": idx + 1,

                "Fabrication No": row?.fabricationNo,

                Store: row?.storeId?.name,

                Status: row?.status,

                "Sent Items": row?.sentItems
                    ?.map(
                        (item) =>
                            `${item?.inventoryId?.masterItem?.partNo} - ${item?.inventoryId?.masterItem?.description} (${item?.quantity})`
                    )
                    .join(" | "),

                "Received Items": row?.receivedItem
                    ?.map(
                        (item) =>
                            `${item?.inventoryId?.masterItem?.partNo} - ${item?.inventoryId?.masterItem?.description} (${item?.quantity})`
                    )
                    .join(" | "),

                "Fabrication Date": row?.fabricationDate
                    ? new Date(row.fabricationDate).toLocaleDateString("en-GB")
                    : "-",
            })),

            "Fabrication"
        );

        // =====================================================
        // EXPORT FILE
        // =====================================================

        XLSX.writeFile(wb, "UniversalSearchResults.xlsx");
    };

    const toggleSection = (key) => {
        setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Inventory rows to display based on arrow state
    const inventoryRowsToShow = showInventoryExtraCols
        ? results.inventoryResults                 // show ALL rows
        : results.inventoryResults.filter(
            row => (row?.currentStock || 0) > 0    // hide zero stock rows
        );

    // ✅ Current Stock total for heading (ALWAYS from full inventory data)
    const inventoryCurrentStockTotal = results.inventoryResults
        .reduce((sum, row) => sum + (row?.currentStock || 0), 0)
        .toFixed(2);

    return (
        <div className="row">
            <div className="col-xl-12">
                <div className="filter cm-content-box box-primary mt-1">
                    <div className="content-title d-flex justify-content-between">
                        <div className="cpa">
                            <i className="fas fa-search me-2" aria-hidden="true"></i>
                            Universal Search
                        </div>
                        <div className="tools">
                            {(
                                results.masterResults.length ||
                                results.inventoryResults.length ||
                                results.itemResults.length ||
                                results.mivResults.length ||
                                results.transferResults.length ||
                                results.castingResults.length ||
                                results.fabricationResults.length

                            ) > 0 && (
                                    <button
                                        className="btn btn-primary btn-sm me-2"
                                        onClick={handleDownloadXLSX}
                                    >
                                        Download XLSX
                                    </button>
                                )}
                            <Link
                                to={"#"}
                                className={`SlideToolHeader ${open2 ? "collapse" : "expand"}`}
                                onClick={() => setOpen2(!open2)}
                            >
                                <i className="fas fa-angle-up"></i>
                            </Link>
                        </div>
                    </div>
                    <Collapse in={open2}>
                        <div className="cm-content-body form excerpt">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <form onSubmit={handleSubmit}>
                                        <div className="row justify-content-center">
                                            <div className="form-group mb-3 col-md-8">
                                                <label>Search By</label>
                                                <select
                                                    className="form-control"
                                                    value={searchType}
                                                    onChange={(e) => setSearchType(e.target.value)}
                                                >
                                                    <option value="partNo">Part No.</option>
                                                    <option value="description">Item Description</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="row justify-content-center">
                                            <div className="form-group mb-3 col-md-8">
                                                <label>Enter Query</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter your search..."
                                                    value={query}
                                                    onChange={(e) => setQuery(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="row justify-content-center">
                                            <div className="form-group mb-3 col-md-8">
                                                {loading ? (
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="submit"
                                                        className="form-control btn btn-primary me-2"
                                                    >
                                                        Submit
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                    </form>

                                    {/* Master Results */}
                                    {searched && results.masterResults.length > 0 && (
                                        <div className="mt-2">
                                            <h4
                                                className="text-primary text-center pt-3 pb-3"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => toggleSection("master")}
                                            >
                                                Master Items {openSections.master ? "▲" : "▼"}
                                            </h4>

                                            {openSections.master ? (
                                                <div className="scrolltable">
                                                    <table className="table table-bordered">
                                                        <thead className="sticky-table-header">
                                                            <tr>
                                                                <th>Part No</th>
                                                                <th>Description</th>
                                                                <th>Category</th>
                                                                <th>Unit</th>
                                                                <th>Rate</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {results.masterResults.map((row, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{row.partNo}</td>
                                                                    <td>{row.description}</td>
                                                                    <td>{row?.category?.name}</td>
                                                                    <td>{row?.unit?.name}</td>
                                                                    <td>{row.rate}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}


                                    {/* Inventory Results */}
                                    {searched && results.inventoryResults.length > 0 && (
                                        <div className="mt-4 pt-1 pb-1">

                                            {/* Inventory Heading (click to toggle) */}
                                            <h4
                                                className="text-primary text-center pt-2 pb-2"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => toggleSection("inventory")}
                                            >
                                                Inventory{" "}
                                                <span className="fw-normal text-dark">
                                                    (Current Stock: {inventoryCurrentStockTotal})
                                                </span>{" "}
                                                {openSections.inventory ? "▲" : "▼"}
                                            </h4>


                                            {/* Inventory Table */}
                                            {openSections.inventory ? (
                                                <div
                                                    className="scrolltable"
                                                >
                                                    <table className="table table-bordered">
                                                        <thead
                                                            className="sticky-table-header"
                                                        >
                                                            <tr>
                                                                <th>#</th>
                                                                <th>Part No</th>
                                                                <th>Description</th>
                                                                <th>Store</th>

                                                                {/* Current Stock + Arrow */}
                                                                <th
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={() => setShowInventoryExtraCols(p => !p)}
                                                                >
                                                                    Current Stock{" "}
                                                                    <span className="ms-1">
                                                                        {showInventoryExtraCols ? "⬅️" : "➡️"}
                                                                    </span>
                                                                </th>

                                                                {/* Extra Columns */}
                                                                {showInventoryExtraCols && (
                                                                    <>
                                                                        <th>Received</th>
                                                                        <th>Transfer</th>
                                                                        <th>Miv</th>
                                                                        <th>Transition Qty</th>
                                                                    </>
                                                                )}
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {inventoryRowsToShow.map((row, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{idx + 1}</td>
                                                                    <td>{row?.masterItem?.partNo}</td>
                                                                    <td>{row?.masterItem?.description}</td>
                                                                    <td>{row?.store?.name}</td>

                                                                    {/* Current Stock always visible */}
                                                                    <td>{row?.currentStock?.toFixed(2)}</td>

                                                                    {/* Extra columns */}
                                                                    {showInventoryExtraCols && (
                                                                        <>
                                                                            <td>{row?.totalRecive?.toFixed(2)}</td>
                                                                            <td>{row?.transfer?.toFixed(2)}</td>
                                                                            <td>{row?.totalMiv?.toFixed(2)}</td>
                                                                            <td>{row?.transitionQty?.toFixed(2)}</td>
                                                                        </>
                                                                    )}
                                                                </tr>
                                                            ))}

                                                            {/* Totals Row */}
                                                            <tr className="fw-bold" style={{ backgroundColor: "#f1f1f1" }}>
                                                                <td colSpan={4} className="text-end">Total</td>

                                                                {/* Current Stock total (based on visible rows) */}
                                                                <td>
                                                                    {inventoryRowsToShow
                                                                        .reduce((sum, row) => sum + (row?.currentStock || 0), 0)
                                                                        .toFixed(2)}
                                                                </td>

                                                                {/* Extra totals only when expanded */}
                                                                {showInventoryExtraCols && (
                                                                    <>
                                                                        <td>
                                                                            {results.inventoryResults
                                                                                .reduce((sum, row) => sum + (row?.totalRecive || 0), 0)
                                                                                .toFixed(2)}
                                                                        </td>
                                                                        <td>
                                                                            {results.inventoryResults
                                                                                .reduce((sum, row) => sum + (row?.transfer || 0), 0)
                                                                                .toFixed(2)}
                                                                        </td>
                                                                        <td>
                                                                            {results.inventoryResults
                                                                                .reduce((sum, row) => sum + (row?.totalMiv || 0), 0)
                                                                                .toFixed(2)}
                                                                        </td>
                                                                        <td>
                                                                            {results.inventoryResults
                                                                                .reduce((sum, row) => sum + (row?.transitionQty || 0), 0)
                                                                                .toFixed(2)}
                                                                        </td>
                                                                    </>
                                                                )}
                                                            </tr>
                                                        </tbody>

                                                    </table>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}


                                    {/* Item Results */}
                                    {searched && results.itemResults.length > 0 && (
                                        <div className="mt-4 pt-1 pb-1">

                                            {/* Item Heading (click to toggle) */}
                                            <h4
                                                className="text-primary text-center pt-4 pb-4"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => toggleSection("items")}
                                            >
                                                Requestion & Item Details {openSections.items ? "▲" : "▼"}
                                            </h4>

                                            {/* Item Table */}
                                            {openSections.items ? (
                                                <div
                                                    className="scrolltable"
                                                >
                                                    <table className="table table-bordered">
                                                        <thead
                                                            className="sticky-table-header"
                                                        >
                                                            <tr className="text-center">
                                                                <th>#</th>
                                                                <th>Req.No</th>
                                                                <th>Part NO.</th>
                                                                <th>Description</th>
                                                                <th>User</th>
                                                                <th>Store</th>
                                                                <th>Qty Required</th>
                                                                <th>Approved</th>
                                                                <th>Received</th>
                                                                <th>Status</th>
                                                                <th>PO</th>
                                                                <th>MRV</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {results.itemResults.map((row, idx) => (
                                                                <tr
                                                                    key={idx}
                                                                    className={
                                                                        row?.status === "approved" ||
                                                                            row?.status === "approvedByAdmin" ||
                                                                            row?.status === "pending"
                                                                            ? "bg-light"
                                                                            : row?.status === "rejected" ||
                                                                                row?.status === "forceclosed" ||
                                                                                row?.status === "autoclosed"
                                                                                ? "bg-danger text-white"
                                                                                : row?.status === "tranfer request"
                                                                                    ? "bg-primary text-white"
                                                                                    : ""
                                                                    }
                                                                >
                                                                    <td>{idx + 1}</td>
                                                                    <td>{row?.requisitionNo?.requisitionNo}</td>
                                                                    <td>{row?.inventory?.masterItem?.partNo}</td>
                                                                    <td>{row?.inventory?.masterItem?.description}</td>
                                                                    <td>{row?.user?.name}</td>
                                                                    <td>{row?.store?.name}</td>
                                                                    <td>{row?.qtyRequired}</td>
                                                                    <td>{row?.approveQty}</td>
                                                                    <td>{row?.qtyRecived}</td>
                                                                    <td>{row?.status}</td>
                                                                    <td>{row?.po?.poNumber || "N/A"}</td>
                                                                    <td>{row?.mrv?.mrvNo || "N/A"}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}

                                    {/* MIV Results */}
                                    {searched && results.mivResults.length > 0 && (
                                        <div className="mt-4 pt-1 pb-1">

                                            {/* MIV Heading (click to toggle) */}
                                            <h4
                                                className="text-primary text-center pt-2 pb-2"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => toggleSection("miv")}
                                            >
                                                Material Issue Voucher & Item Details {openSections.miv ? "▲" : "▼"}
                                            </h4>

                                            {/* MIV Table */}
                                            {openSections.miv ? (
                                                <div
                                                    className="scrolltable"
                                                >
                                                    <table className="table table-bordered">
                                                        <thead
                                                            className="sticky-table-header"
                                                        >
                                                            <tr className="text-center">
                                                                <th>#</th>
                                                                <th>MIV No</th>
                                                                <th>Part NO.</th>
                                                                <th>Description</th>
                                                                <th>User</th>
                                                                <th>Store</th>
                                                                <th>Used Qty</th>
                                                                <th>Approved Qty</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {results.mivResults.map((row, idx) => (
                                                                <tr
                                                                    key={idx}
                                                                    className={
                                                                        row?.status === "rejected" ||
                                                                            row?.status === "forceclosed" ||
                                                                            row?.status === "autoclosed"
                                                                            ? "bg-danger text-white"
                                                                            : row?.status === "pending"
                                                                                ? "bg-primary text-white"
                                                                                : ""
                                                                    }
                                                                >
                                                                    <td>{idx + 1}</td>
                                                                    <td>{row?.miv?.mivNo}</td>
                                                                    <td>{row?.inventory?.masterItem?.partNo}</td>
                                                                    <td>{row?.inventory?.masterItem?.description}</td>
                                                                    <td>{row?.user?.name}</td>
                                                                    <td>{row?.miv?.store?.name}</td>
                                                                    <td>{row?.usedQty}</td>
                                                                    <td>{row?.approveQty}</td>
                                                                    <td>{row?.status}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}

                                    {/* Transfer Results */}
                                    {searched && results.transferResults.length > 0 && (
                                        <div className="mt-4 pt-1 pb-1">

                                            {/* Transfer Heading (click to toggle) */}
                                            <h4
                                                className="text-primary text-center pt-2 pb-2"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => toggleSection("transfer")}
                                            >
                                                Material Transfer Voucher & Item Details {openSections.transfer ? "▲" : "▼"}
                                            </h4>

                                            {/* Transfer Table */}
                                            {openSections.transfer ? (
                                                <div
                                                    className="scrolltable"
                                                >
                                                    <table className="table table-bordered">
                                                        <thead
                                                            className="sticky-table-header"
                                                        >
                                                            <tr className="text-center">
                                                                <th>#</th>
                                                                <th>Transfer No</th>
                                                                <th>Part NO.</th>
                                                                <th>Description</th>
                                                                <th>User</th>
                                                                <th>From</th>
                                                                <th>To</th>
                                                                <th>Transfer Qty</th>
                                                                <th>Approved Qty</th>
                                                                <th>Received Qty</th>
                                                                <th>Status</th>
                                                                <th>MRV</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {results.transferResults.map((row, idx) => (
                                                                <tr
                                                                    key={idx}
                                                                    className={
                                                                        row?.status === "rejected" ||
                                                                            row?.status === "forceclosed" ||
                                                                            row?.status === "autoclosed"
                                                                            ? "bg-danger text-white"
                                                                            : row?.status === "pending" || row?.status === "approved"
                                                                                ? "bg-primary text-white"
                                                                                : ""
                                                                    }
                                                                >
                                                                    <td>{idx + 1}</td>
                                                                    <td>{row?.transfer?.transferNo}</td>
                                                                    <td>{row?.inventory?.masterItem?.partNo}</td>
                                                                    <td>{row?.inventory?.masterItem?.description}</td>
                                                                    <td>{row?.user?.name}</td>
                                                                    <td>{row?.transfer?.storeFrom?.name}</td>
                                                                    <td>{row?.transfer?.storeTo?.name}</td>
                                                                    <td>{row?.transferQty}</td>
                                                                    <td>{row?.approveQty}</td>
                                                                    <td>{row?.recived}</td>
                                                                    <td>{row?.status}</td>
                                                                    <td>{row?.mrv?.mrvNo}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}


                                    {/* Casting Results */}
                                    {searched && results.castingResults.length > 0 && (
                                        <div className="mt-4 pt-1 pb-1">

                                            {/* Casting Heading (click to toggle) */}
                                            <h4
                                                className="text-primary text-center pt-2 pb-2"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => toggleSection("casting")}
                                            >
                                                Casting & Item Details {openSections.casting ? "▲" : "▼"}
                                            </h4>

                                            {/* Casting Table */}
                                            {openSections.casting ? (
                                                <div
                                                    className="scrolltable"
                                                >
                                                    <table className="table table-bordered">
                                                        <thead
                                                            className="sticky-table-header"
                                                        >
                                                            <tr className="text-center">
                                                                <th>#</th>
                                                                <th>Request No</th>
                                                                <th>Request Type</th>
                                                                <th>Part NO.</th>
                                                                <th>Description</th>
                                                                <th>User</th>
                                                                <th>Store Name</th>
                                                                <th>Approved</th>
                                                                <th>Received</th>
                                                                <th>Casting</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {results.castingResults.map((row, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{idx + 1}</td>
                                                                    <td>{row?.RequestNo}</td>
                                                                    <td>{row?.RequestType}</td>
                                                                    <td>{row?.inventory?.masterItem?.partNo}</td>
                                                                    <td>{row?.inventory?.masterItem?.description}</td>
                                                                    <td>{row?.user?.name}</td>
                                                                    <td>{row?.storeName}</td>
                                                                    <td>{row?.approveQty}</td>
                                                                    <td>{row?.qtyRecived}</td>
                                                                    <td>{row?.castingQty}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}


                                    {/* Asset Results */}
                                    {searched && results.assetResults.length > 0 && (
                                        <div className="mt-4 pt-1 pb-1">

                                            {/* Asset Heading (click to toggle) */}
                                            <h4
                                                className="text-primary text-center pt-2 pb-2"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => toggleSection("asset")}
                                            >
                                                Asset Details {openSections.asset ? "▲" : "▼"}
                                            </h4>

                                            {/* Asset Table */}
                                            {openSections.asset ? (
                                                <div
                                                    className="scrolltable"
                                                >
                                                    <table className="table table-bordered">
                                                        <thead
                                                            className="sticky-table-header"
                                                        >
                                                            <tr className="text-center">
                                                                <th>#</th>
                                                                <th>Part No</th>
                                                                <th>Description</th>
                                                                <th>Store</th>
                                                                <th>Vendor</th>
                                                                <th>Serial Number</th>
                                                                <th>Model</th>
                                                                <th>Purchased Amount</th>
                                                                <th>Condition</th>
                                                                <th>Status</th>
                                                                <th>Assigned To</th>
                                                                <th>Purchase Date</th>
                                                                <th>Warranty Expiry</th>
                                                                <th>Remarks</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {results.assetResults.map((row, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{idx + 1}</td>
                                                                    <td>{row?.inventory?.masterItem?.partNo || "-"}</td>
                                                                    <td>{row?.inventory?.masterItem?.description || "-"}</td>
                                                                    <td>{row?.store?.name || "-"}</td>
                                                                    <td>{row?.vendor || "-"}</td>
                                                                    <td>{row?.serialNumber || "-"}</td>
                                                                    <td>{row?.model || "-"}</td>
                                                                    <td>
                                                                        {row?.purchasedAmount
                                                                            ? Number(row.purchasedAmount).toLocaleString("en-IN", {
                                                                                style: "currency",
                                                                                currency: "INR",
                                                                                minimumFractionDigits: 2,
                                                                            })
                                                                            : "-"}
                                                                    </td>
                                                                    <td>{row?.condition || "-"}</td>
                                                                    <td>{row?.status || "-"}</td>
                                                                    <td>{row?.assignedTo?.name || "-"}</td>
                                                                    <td>
                                                                        {row?.purchaseDate
                                                                            ? new Date(row.purchaseDate).toLocaleDateString("en-GB")
                                                                            : "-"}
                                                                    </td>
                                                                    <td>
                                                                        {row?.warrantyExpiry
                                                                            ? new Date(row.warrantyExpiry).toLocaleDateString("en-GB")
                                                                            : "-"}
                                                                    </td>
                                                                    <td>{row?.remarks || "-"}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                    {/* Fabrication Results */}
                                    {searched && results.fabricationResults.length > 0 && (
                                        <div className="mt-4 pt-1 pb-1">

                                            {/* Fabrication Heading */}
                                            <h4
                                                className="text-primary text-center pt-2 pb-2"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => toggleSection("fabrication")}
                                            >
                                                Fabrication Details {openSections.fabrication ? "▲" : "▼"}
                                            </h4>

                                            {/* Fabrication Table */}
                                            {openSections.fabrication ? (
                                                <div
                                                    className="scrolltable"
                                                >
                                                    <table className="table table-bordered">

                                                        <thead
                                                            className="sticky-table-header"
                                                        >
                                                            <tr className="text-center">
                                                                <th>#</th>
                                                                <th>Fabrication No</th>
                                                                <th>Store</th>
                                                                <th>Status</th>
                                                                <th>Sent Items</th>
                                                                <th>Received Items</th>
                                                                <th>Date</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {results.fabricationResults.map((row, idx) => (

                                                                <tr key={idx}>

                                                                    <td>{idx + 1}</td>

                                                                    <td>{row?.fabricationNo}</td>

                                                                    <td>{row?.storeId?.name}</td>

                                                                    <td>{row?.status}</td>

                                                                    <td>
                                                                        {row?.sentItems?.map((item, i) => (
                                                                            <div key={i}>
                                                                                {item?.inventoryId?.masterItem?.partNo}
                                                                                {" - "}
                                                                                {item?.inventoryId?.masterItem?.description}
                                                                                {" ("}
                                                                                {item?.quantity}
                                                                                {")"}
                                                                            </div>
                                                                        ))}
                                                                    </td>

                                                                    <td>
                                                                        {row?.receivedItem?.map((item, i) => (
                                                                            <div key={i}>
                                                                                {item?.inventoryId?.masterItem?.partNo}
                                                                                {" - "}
                                                                                {item?.inventoryId?.masterItem?.description}
                                                                                {" ("}
                                                                                {item?.quantity}
                                                                                {")"}
                                                                            </div>
                                                                        ))}
                                                                    </td>

                                                                    <td>
                                                                        {row?.fabricationDate
                                                                            ? new Date(row.fabricationDate).toLocaleDateString("en-GB")
                                                                            : "-"}
                                                                    </td>

                                                                </tr>
                                                            ))}
                                                        </tbody>

                                                    </table>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}





                                </div>
                            </div>
                        </div>
                    </Collapse>
                </div>
            </div>
        </div>
    );
};

export default UniversalSearch;
